import React from 'react';
import Icon from '../../../components/AppIcon';

const EngagementScoreCard = ({ score }) => {
  if (!score) return null;

  const getScoreColor = (level) => {
    switch (level) {
      case 'high':
        return 'text-success';
      case 'medium':
        return 'text-primary';
      case 'low':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  const getScoreIcon = (level) => {
    switch (level) {
      case 'high':
        return 'TrendingUp';
      case 'medium':
        return 'Activity';
      case 'low':
        return 'TrendingDown';
      default:
        return 'Minus';
    }
  };

  const scorePercentage = Math.min(score?.score || 0, 100);

  return (
    <div className="bg-gradient-to-br from-primary/10 to-success/10 rounded-xl border border-primary/20 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Target" size={20} className="text-primary" />
        <h2 className="text-lg font-heading font-semibold text-foreground">Your Engagement Score</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Score Visualization */}
        <div className="flex flex-col items-center justify-center">
          <div className="relative w-32 h-32">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                className="text-muted"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="transparent"
                strokeDasharray={`${2 * Math.PI * 56}`}
                strokeDashoffset={`${2 * Math.PI * 56 * (1 - scorePercentage / 100)}`}
                className={getScoreColor(score?.level)}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-3xl font-bold ${getScoreColor(score?.level)}`}>
                {Math.round(scorePercentage)}
              </span>
              <span className="text-xs text-muted-foreground">out of 100</span>
            </div>
          </div>
        </div>

        {/* Score Details */}
        <div className="md:col-span-2 space-y-4">
          <div className="flex items-center gap-3">
            <Icon name={getScoreIcon(score?.level)} size={24} className={getScoreColor(score?.level)} />
            <div>
              <p className="text-sm text-muted-foreground">Engagement Level</p>
              <p className={`text-xl font-bold capitalize ${getScoreColor(score?.level)}`}>
                {score?.level}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Icon name="Calendar" size={24} className="text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Days Tracked</p>
              <p className="text-xl font-bold text-foreground">{score?.daysTracked || 0}</p>
            </div>
          </div>

          <div className="p-3 bg-card/50 rounded-lg">
            <p className="text-xs text-muted-foreground">
              {score?.level === 'high' && 'Excellent engagement! You\'re making the most of MyBodyToday.'}
              {score?.level === 'medium' && 'Good engagement. Try exploring more features to improve your score.'}
              {score?.level === 'low' && 'Low engagement detected. Set up daily reminders to stay on track.'}
              {score?.level === 'inactive' && 'No recent activity. Start tracking your symptoms today!'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EngagementScoreCard;